/* Lemonlords order tracking — storefront UI (vanilla JS, no dependencies).
 *
 * Mount point: <div data-llt data-api-base="https://…" data-support-url="…">
 * States: form → loading → result | error. Signed links (?o=&t=) skip the form.
 * All API text is inserted with textContent, never innerHTML.
 */
(() => {
  'use strict';

  const STEPS = ['Confirmed', 'Shipped', 'Out for delivery', 'Delivered'];
  const TONE = { DELIVERED: 'success', DELIVERY_ATTEMPTED: 'warning', RTO: 'danger', EXCEPTION: 'danger' };
  const TIMEOUT_MS = 15000;
  const TZ = 'Asia/Kolkata';

  const fmtDay = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' });
  const fmtTime = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  const fmtKey = new Intl.DateTimeFormat('en-CA', { timeZone: TZ }); // YYYY-MM-DD
  const fmtFull = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const fmtCancelled = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

  const ICON = {
    alert: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 7v6M12 17h.01"/></svg>',
    truck: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M1 6h13v10H1zM14 9h4l3 3v4h-7z"/><circle cx="5.5" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>',
    checkFilled: '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M7.5 12.5l3 3 6-6.5" fill="none" stroke="var(--llt-bg)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    circle: '<svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>',
    info: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 11v6M12 7h.01"/></svg>',
  };

  // ---------- helpers ----------

  function h(tag, attrs, ...children) {
    const el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null || v === false) continue;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k === 'html') el.innerHTML = v; // trusted constants only (icons)
      else if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
      else el.setAttribute(k, v === true ? '' : v);
    }
    for (const c of children.flat()) {
      if (c == null || c === false) continue;
      el.append(c instanceof Node ? c : document.createTextNode(String(c)));
    }
    return el;
  }

  function parseDate(value) {
    if (!value) return null;
    // Plain dates (ETA) are calendar days in IST.
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00+05:30` : value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  function dayLabel(d) {
    const key = fmtKey.format(d);
    const now = new Date();
    if (key === fmtKey.format(now)) return 'Today';
    if (key === fmtKey.format(new Date(now.getTime() - 864e5))) return 'Yesterday';
    return fmtDay.format(d);
  }

  const timeLabel = (d) => fmtTime.format(d).replace(/\s?(am|pm)$/i, (m) => m.toLowerCase());

  function normaliseOrder(raw) {
    const v = String(raw || '').trim().replace(/^#/, '').replace(/\s+/g, '');
    return /^[A-Za-z0-9-]{1,20}$/.test(v) ? v : null;
  }

  function parseContact(raw) {
    const v = String(raw || '').trim();
    if (v.includes('@')) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? { email: v.toLowerCase() } : null;
    const digits = v.replace(/\D/g, '');
    return digits.length >= 10 ? { phone: digits.slice(-10) } : null;
  }

  // ---------- app ----------

  function mount(root) {
    if (root.dataset.lltReady) return;
    root.dataset.lltReady = '1';

    const cfg = {
      apiBase: (root.dataset.apiBase || '').replace(/\/$/, ''),
      supportUrl: root.dataset.supportUrl || '',
      supportLabel: root.dataset.supportLabel || 'Contact support',
      helpText: root.dataset.helpText || 'Questions about your order?',
    };
    const app = root.querySelector('[data-llt-app]') || root;
    const live = h('p', { class: 'llt-sr', 'aria-live': 'polite' });
    root.append(live);

    let lastRequest = null;
    let lastValues = { order: '', contact: '' };

    const announce = (msg) => { live.textContent = msg; };
    const show = (...nodes) => { app.replaceChildren(...nodes); app.removeAttribute('aria-busy'); };

    function supportLink(text) {
      return cfg.supportUrl ? h('a', { href: cfg.supportUrl }, text || cfg.supportLabel) : null;
    }

    function notice(tone, ...content) {
      return h('div', { class: `llt-notice${tone ? ` llt-notice--${tone}` : ''}`, role: tone === 'danger' ? 'alert' : null },
        h('span', { html: tone ? ICON.alert : ICON.info }),
        h('div', null, ...content));
    }

    // ----- form -----
    function renderForm({ message, tone = 'danger', errors = {} } = {}) {
      const orderErr = errors.order && h('span', { class: 'llt-field__error', id: 'llt-order-err', text: errors.order });
      const contactErr = errors.contact && h('span', { class: 'llt-field__error', id: 'llt-contact-err', text: errors.contact });

      const orderInput = h('input', {
        id: 'llt-order', name: 'order', type: 'text', inputmode: 'text', autocomplete: 'off',
        placeholder: 'e.g. #16623', value: lastValues.order, required: true,
        'aria-invalid': errors.order ? 'true' : null, 'aria-describedby': errors.order ? 'llt-order-err' : null,
      });
      const contactInput = h('input', {
        id: 'llt-contact', name: 'contact', type: 'text', autocomplete: 'email',
        placeholder: 'you@example.com or 98xxxxxxxx', value: lastValues.contact, required: true,
        'aria-invalid': errors.contact ? 'true' : null,
        'aria-describedby': errors.contact ? 'llt-contact-err' : 'llt-contact-hint',
      });

      const form = h('form', { class: 'llt-form', novalidate: true, onsubmit: onSubmit },
        message && notice(tone, message),
        h('div', { class: 'llt-field' },
          h('label', { for: 'llt-order', text: 'Order number' }), orderInput, orderErr),
        h('div', { class: 'llt-field' },
          h('label', { for: 'llt-contact', text: 'Email or phone number' }), contactInput,
          contactErr || h('span', { class: 'llt-field__hint', id: 'llt-contact-hint', text: 'The one you used when placing the order.' })),
        h('button', { class: 'llt-btn', type: 'submit', text: 'Track order' }));

      show(form);
      if (message) announce(message);
      (errors.order ? orderInput : errors.contact ? contactInput : null)?.focus();
    }

    function onSubmit(e) {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      lastValues = { order: String(fd.get('order') || ''), contact: String(fd.get('contact') || '') };
      const order = normaliseOrder(lastValues.order);
      const contact = parseContact(lastValues.contact);
      const errors = {};
      if (!order) errors.order = 'Enter your order number, e.g. #16623.';
      if (!contact) errors.contact = 'Enter the email or 10-digit phone number used for this order.';
      if (errors.order || errors.contact) return renderForm({ errors });
      // Customer details go in the POST body, never the URL (keeps them out of logs and history).
      load({ method: 'POST', body: { order, ...contact } }, 'form');
    }

    // ----- loading -----
    function renderLoading() {
      app.setAttribute('aria-busy', 'true');
      app.replaceChildren(h('div', { class: 'llt-card llt-skeleton', 'aria-hidden': 'true' },
        h('span', { style: 'width:40%' }), h('span', { style: 'width:70%;height:22px' }),
        h('span', { style: 'width:55%' }), h('span', { style: 'width:100%;height:30px;margin-top:20px' }),
        h('span', { style: 'width:85%' }), h('span', { style: 'width:65%' })));
      announce('Looking up your order…');
    }

    // ----- network -----
    async function load(request, source) {
      lastRequest = { request, source };
      renderLoading();
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
      let res;
      let data = null;
      try {
        const url = `${cfg.apiBase}/api/track${request.query ? `?${new URLSearchParams(request.query)}` : ''}`;
        res = await fetch(url, {
          method: request.method,
          headers: request.body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
          body: request.body ? JSON.stringify(request.body) : undefined,
          signal: ctrl.signal,
        });
        data = await res.json().catch(() => null);
      } catch {
        return renderError();
      } finally {
        clearTimeout(timer);
      }

      if (res.ok && data) return renderResult(data);

      const message = data && data.message;
      if (source === 'link' && (res.status === 400 || res.status === 404)) {
        forgetLink();
        return renderForm({ tone: null, message: 'This tracking link has expired or is incomplete. Enter your order details below to see the latest status.' });
      }
      if (source === 'awb' && (res.status === 400 || res.status === 404)) {
        clearAwbParam();
        return renderForm({ tone: null, message: "We couldn't find tracking for that shipment yet. Enter your order details below to see the latest status." });
      }
      if (res.status === 400 || res.status === 404) {
        return renderForm({ message: message || "We couldn't find an order matching those details. Check the order number and the email or phone you used." });
      }
      if (res.status === 429) {
        return renderForm({ tone: 'warning', message: message || 'Too many attempts. Please wait a minute and try again.' });
      }
      renderError(message);
    }

    function clearLinkParams() {
      const url = new URL(window.location.href);
      url.searchParams.delete('o');
      url.searchParams.delete('t');
      window.history.replaceState(null, '', url);
    }

    // The signed link is a key to this order's tracking. Take it out of the
    // address bar straight away (analytics and ad scripts record page URLs) and
    // keep it for this tab only, so a refresh still shows the order.
    const LINK_KEY = 'llt-link';
    const savedLink = {
      get() {
        try {
          const v = JSON.parse(sessionStorage.getItem(LINK_KEY) || 'null');
          return v && typeof v.o === 'string' && typeof v.t === 'string' ? v : null;
        } catch { return null; }
      },
      set(v) { try { sessionStorage.setItem(LINK_KEY, JSON.stringify(v)); } catch {} },
      clear() { try { sessionStorage.removeItem(LINK_KEY); } catch {} },
    };

    function forgetLink() {
      savedLink.clear();
      clearLinkParams();
      clearAwbParam();
    }

    function clearAwbParam() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('awb')) return;
      url.searchParams.delete('awb');
      window.history.replaceState(null, '', url);
    }

    // ----- error -----
    function renderError(message) {
      show(h('div', { class: 'llt-card' },
        h('h2', { class: 'llt-status', tabindex: '-1', text: "We can't load tracking right now" }),
        h('p', { class: 'llt-eta', text: message || 'Our delivery partner is not responding. Please try again in a few minutes.' }),
        h('div', { style: 'display:flex;gap:12px;flex-wrap:wrap' },
          h('button', { class: 'llt-btn', type: 'button', text: 'Try again', onclick: () => lastRequest && load(lastRequest.request, lastRequest.source) }),
          h('button', { class: 'llt-btn llt-btn--ghost', type: 'button', text: 'Back', onclick: () => renderForm() }))));
      app.querySelector('h2').focus();
      announce("We can't load tracking right now.");
    }

    // ----- result -----
    function renderResult(data) {
      const shipments = Array.isArray(data.shipments) ? data.shipments : [];
      // AWB links (WhatsApp, emails) carry no order details — title by shipment instead.
      const title = data.awbOnly ? 'Your shipment' : `Order ${data.order || ''}`.trim();
      const heading = h('h2', { class: 'llt-result__order', tabindex: '-1', text: title });

      const placed = parseDate(data.placedAt);
      const nodes = [
        h('div', { class: 'llt-result__top' },
          h('div', null, heading,
            placed && h('p', { class: 'llt-result__placed' }, 'Placed on ', h('time', { datetime: data.placedAt, text: fmtFull.format(placed) }))),
          h('button', { class: 'llt-link', type: 'button', text: 'Track another order', onclick: () => { forgetLink(); renderForm(); } })),
      ];

      // Same layout and wording as the My account "Live tracking" block
      // (shopify-app/extensions/order-tracking), so both look alike.
      // Wording comes from the server (Customer messages page); built-in text is the fallback.
      const notice0 = data.notice || (shipments.length && data.cancelled
        ? { eyebrow: 'Order status', title: 'Your order is cancelled', text: `${cancelledOn(data) ? `Cancelled on ${cancelledOn(data)}. ` : ''}The courier updates for this order are below.`, step: 0 }
        : null);
      const waiting = shipments.length === 0 ? data.message || noShipmentState(data) : null;
      const card = h('section', { class: 'llt-card llt-live' });
      if (shipments.length && notice0) card.append(messageBlock(notice0));
      if (waiting) card.append(messageBlock(waiting));
      shipments.forEach((s, i) => card.append(shipmentBlock(s, i, shipments.length)));
      nodes.push(card);

      if (Array.isArray(data.items) && data.items.length) nodes.push(itemsCard(data.items));
      if (cfg.supportUrl) nodes.push(h('p', { class: 'llt-help' }, `${cfg.helpText} `, supportLink()));

      show(...nodes);
      heading.focus();
      const first = shipments[0];
      announce(`${title}: ${first ? first.label : waiting.title}`);
    }

    // "15 Oct, 7:35 pm" in India time, or '' if unknown.
    function cancelledOn(data) {
      const d = parseDate(data.cancelledAt);
      return d ? fmtCancelled.format(d).replace(/(AM|PM)/, (m) => m.toLowerCase()) : '';
    }

    // What to say when there's no tracking number to show.
    function noShipmentState(data) {
      if (data.cancelled) {
        const on = cancelledOn(data);
        return { eyebrow: 'Order status', title: 'Your order is cancelled', text: `${on ? `Cancelled on ${on}. ` : ''}If you have any questions, please contact us.`, step: 0 };
      }
      if (data.fulfillmentStatus === 'FULFILLED') return { title: 'Shipped', text: "Your order has shipped. Tracking details aren't available for this shipment.", step: 2 };
      if (data.preOrder) return { title: 'Pre-order', text: "This is a pre-order and will ship as soon as it's ready. Tracking appears here when it ships.", step: 1 };
      return { title: 'Preparing your order', text: "We're packing your order. Tracking appears here as soon as it ships.", step: 1 };
    }

    // Small italic eyebrow + big uppercase heading.
    function hero(eyebrow, title, tone) {
      return h('div', { class: 'llt-hero' },
        h('p', { class: 'llt-hero__eyebrow', text: eyebrow }),
        h('h3', { class: `llt-hero__title${tone ? ` llt-hero__title--${tone}` : ''}`, text: title }));
    }

    function messageBlock(m) {
      return h('div', { class: 'llt-block' },
        hero(m.eyebrow || 'Live tracking', m.title),
        h('p', { class: 'llt-text llt-text--muted', text: m.text }),
        m.step ? progress(m.step) : null);
    }

    /** The grey "Arriving by" panel: [label, date], or null. */
    function datePanel(s, events) {
      if (s.status === 'DELIVERED') {
        const d = events[0]?.date || null;
        return d ? ['Delivered on', fmtDay.format(d)] : null;
      }
      if (s.status === 'RTO' || s.status === 'EXCEPTION') return null;
      const eta = parseDate(s.eta);
      if (!eta) return null;
      const key = fmtKey.format(eta);
      const now = new Date();
      if (key === fmtKey.format(now)) return ['Arriving', 'Today'];
      if (key === fmtKey.format(new Date(now.getTime() + 864e5))) return ['Arriving', 'Tomorrow'];
      return [key < fmtKey.format(now) ? 'Was expected by' : 'Arriving by', fmtDay.format(eta)];
    }

    /** A sentence for states the date panel can't explain. */
    function statusNote(s, events, panel) {
      if (s.status === 'RTO') {
        const back = events[0] && events[0].label === 'Returned to Lemonlords' ? events[0].date : null;
        return back
          ? `This package was returned to us on ${fmtDay.format(back)}. Please contact support for a refund or to have it sent again.`
          : 'This package is on its way back to us. Please contact support.';
      }
      if (s.status === 'EXCEPTION') return 'This package is delayed. Please contact support if you need help.';
      if (s.status === 'DELIVERY_ATTEMPTED' && !s.attempts) return "The courier couldn't deliver and will try again. Please keep your phone reachable.";
      if (s.status !== 'DELIVERED' && !panel) return "We'll show a delivery date once the courier confirms it.";
      return '';
    }

    function attemptsNotice(s) {
      if (!s.attempts || s.status === 'DELIVERED' || s.status === 'RTO') return null;
      const max = s.maxAttempts || 3;
      const left = max - s.attempts;
      return left <= 0
        ? notice('danger', `All ${max} delivery attempts have been made. Please contact us so your order isn't returned.`)
        : notice('warning', `Delivery attempt ${s.attempts} of ${max} didn't go through. ${s.carrier || 'The courier'} will try ${left} more time${left === 1 ? '' : 's'}. Please keep your phone reachable.`);
    }

    function shipmentBlock(s, index, total) {
      const tone = TONE[s.status] || null;
      const events = (Array.isArray(s.events) ? s.events : [])
        .map((e) => ({ ...e, date: parseDate(e.time) }))
        .filter((e) => e.date)
        .sort((a, b) => b.date - a.date);
      const panel = datePanel(s, events);
      const note = statusNote(s, events, panel);

      const block = h('div', { class: 'llt-block', 'aria-label': total > 1 ? `Package ${index + 1} of ${total}` : null });
      block.append(hero(total > 1 ? `Live tracking · package ${index + 1} of ${total}` : 'Live tracking', s.label || 'In transit', tone));
      if (panel) {
        block.append(h('div', { class: 'llt-date' },
          h('div', null, h('span', { class: 'llt-date__label', text: panel[0] }), h('strong', { class: 'llt-date__value', text: panel[1] })),
          h('span', { class: 'llt-date__icon', html: ICON.truck })));
      }
      if (note) block.append(h('p', { class: 'llt-text', text: note }));
      const att = attemptsNotice(s);
      if (att) block.append(att);
      if (s.step != null) block.append(progress(s.step));

      const copy = h('button', { class: 'llt-copy', type: 'button', text: 'Copy', 'aria-label': `Copy tracking number ${s.awb}` });
      copy.addEventListener('click', () => {
        navigator.clipboard?.writeText(s.awb).then(() => { copy.textContent = 'Copied'; setTimeout(() => { copy.textContent = 'Copy'; }, 1600); }, () => {});
      });
      block.append(h('div', { class: 'llt-courier' },
        h('div', null, h('strong', { class: 'llt-courier__name', text: s.carrier || 'Courier' }), h('span', { class: 'llt-courier__awb', text: `AWB ${s.awb}` })),
        s.awb ? copy : null));

      if (events.length) block.append(timeline(events));
      if (s.stale) block.append(h('p', { class: 'llt-updated', text: 'Live updates are paused. Showing the latest saved update.' }));
      return block;
    }

    /** Four bar segments, filled up to the current step, labels underneath. */
    function progress(step) {
      const list = h('ol', { class: 'llt-progress', 'aria-label': `Step ${step} of 4: ${STEPS[step - 1]}` });
      STEPS.forEach((label, i) => {
        const n = i + 1;
        list.append(h('li', { class: n <= step ? 'is-done' : null, 'aria-current': n === step ? 'step' : null }, label));
      });
      return list;
    }

    function timeline(events) {
      const wrap = h('div', { class: 'llt-timeline' }, h('h4', { class: 'llt-timeline__title', text: 'Tracking history' }));
      let currentDay = null;
      let list = null;
      events.forEach((e, i) => {
        const day = dayLabel(e.date);
        if (day !== currentDay) {
          currentDay = day;
          list = h('ol');
          wrap.append(h('p', { class: 'llt-timeline__day', text: day }), list);
        }
        list.append(h('li', { class: `llt-event${i === 0 ? ' is-latest' : ''}` },
          h('span', { class: 'llt-event__icon', html: i === 0 ? ICON.checkFilled : ICON.circle }),
          h('span', null,
            h('span', { class: 'llt-event__label', text: e.label || 'Update' }),
            h('span', { class: 'llt-event__meta', text: [timeLabel(e.date), e.location].filter(Boolean).join(' · ') }))));
      });
      return wrap;
    }

    function itemsCard(items) {
      return h('section', { class: 'llt-card' },
        h('h3', { class: 'llt-card__heading', text: 'In this order' }),
        h('ul', { class: 'llt-items' }, items.map((it) => h('li', { class: 'llt-item' },
          it.image ? h('img', { src: it.image, alt: '', loading: 'lazy', width: '56', height: '70' }) : h('span', { class: 'llt-item__ph' }),
          h('span', null,
            h('span', { class: 'llt-item__title', text: it.title || '' }),
            h('span', { class: 'llt-item__qty', text: `Qty ${it.quantity ?? 1}` }))))));
    }

    // ----- start -----
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('o') && params.get('t') ? { o: params.get('o'), t: params.get('t') } : null;
    if (fromUrl) {
      savedLink.set(fromUrl);
      clearLinkParams();
    }
    // AWB links (Spur WhatsApp, emails): the AWB is already public on the
    // courier's own site, so it can stay in the address bar for refreshes.
    const awb = (params.get('awb') || '').trim();
    const link = fromUrl || savedLink.get();
    if (/^\d{8,20}$/.test(awb)) load({ method: 'GET', query: { awb } }, 'awb');
    else if (link) load({ method: 'GET', query: link }, 'link');
    else renderForm();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-llt]').forEach(mount);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  // Re-mount when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
